// Composant Gestion des commandes
import { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import CommandsQueries from '../../../../requests/queries/CommandsQueries';
import LoadingSpinner from '../../../common/Loading';
import { convertStatusToFrench } from '../../../../utils/utils';

export default function CommandsManagement() {

    const [commands, setCommands] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('all');
    const [sortedBy, setSortedBy] = useState('created_at');
    const [currentCommand, setCurrentCommand] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');
 
    useEffect(() => {
        setIsLoading(true);
        fetchCommands();
    }, []);

    const fetchCommands = async () => {
        setIsLoading(true);
        try {
            const res = await CommandsQueries.findAll();
            setCommands(res.data.commands);
            setIsLoading(false);
        } catch (err) {
            setError(err.response?.data?.message || 'Une erreur est survenue');
            setIsLoading(false);
        }
    };

    const handleSort = (column) => {
        if (sortedBy === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortedBy(column);
            setSortDirection('desc');
        }
    };

    if(isLoading) {
        return <LoadingSpinner />;
    }

    if(error) {
        return <div className="alert alert-danger">{error}</div>;
    }

    return (
      <div className="orders-section">
        <div className="section-header">
          <h2>Gestion des Commandes</h2>
          <div className="filter-options">
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className="form-control">
              <option value={"all"}>Toutes les commandes</option>
              <option value="paid">Payées</option>
              <option value="delivered">Livrées</option>
            </select>
          </div>
        </div>
  
        <div className="orders-list">
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('number')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <i className={`bi bi-arrow-${sortedBy === 'number' ? (sortDirection === 'asc' ? 'up' : 'down') : 'down'}`}></i> N° Commande
                  </th>
                  <th>Client</th>
                  <th onClick={() => handleSort('created_at')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <i className={`bi bi-arrow-${sortedBy === 'created_at' ? (sortDirection === 'asc' ? 'up' : 'down') : 'down'}`}></i> Date
                  </th>
                  <th onClick={() => handleSort('status')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <i className={`bi bi-arrow-${sortedBy === 'status' ? (sortDirection === 'asc' ? 'up' : 'down') : 'down'}`}></i> Statut
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {
                    commands.sort((a, b) => {
                        if(sortedBy === 'number') {
                            return sortDirection === 'asc' ? a.number - b.number : b.number - a.number;
                        }
                        if(sortedBy === 'created_at') {
                            return sortDirection === 'asc' ? new Date(a.created_at).getTime() - new Date(b.created_at).getTime() : new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
                        }
                        if(sortedBy === 'status') {
                            return sortDirection === 'asc' ? a.status.localeCompare(b.status) : b.status.localeCompare(a.status);
                        }
                        return 0;
                    }).map((command) => {
                        if(filter !== "all" && (command.status.toLowerCase() !== filter.toLowerCase())) {
                            return null;
                        }
                        return (
                            <tr key={command.id}>
                                <td>#CMD-{command.number}</td>
                                <td>{command.client?.email || command.client_id || 'N/A'}</td>
                                <td>{new Date(command.updated_at || command.created_at).toLocaleDateString()}</td>
                                <td>
                                    <span className={`badge ${
                                        command.status === "delivered" ? "bg-success" : 
                                        command.status === "paid" ? "bg-info" : 
                                        "bg-warning"
                                    }`}>
                                        {convertStatusToFrench(command.status)}
                                    </span>
                                </td>
                                <td>
                                    <button className="btn btn-sm btn-outline-primary" onClick={() => setCurrentCommand(command)}>Voir</button>
                                </td>
                            </tr>
                        )
                    })
                }
              </tbody>
            </table>
          </div>
        </div>
        {currentCommand && ReactDOM.createPortal(
            <CommandDetailsModal command={currentCommand} error={error} setError={setError} setCommand={setCurrentCommand} fetchCommands={fetchCommands} />,
            document.body
        )}
      </div>
    );
  };

function CommandDetailsModal({ command, setCommand, error, setError, fetchCommands }) {
    if (!command) return null;

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            setCommand(null);
        }
    };

    const handleMarkAsDelivered = async () => {
        try {
            await CommandsQueries.update(command.id, { status: 'delivered' });
            setCommand(null);
            fetchCommands();
        } catch (err) {
            console.error('Erreur lors de la mise à jour du statut de la commande:', err);
            setError(err.response?.data?.message || 'Une erreur est survenue');
        }
    };

    return (
        <div 
            className="modal fade show" 
            style={{
                position: 'fixed', 
                top: 0, 
                left: 0, 
                right: 0,
                bottom: 0,
                width: '100vw', 
                height: '100vh', 
                backgroundColor: 'rgba(0, 0, 0, 0.5)', 
                zIndex: 10000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: 0,
                padding: 0,
                border: 'none',
                outline: 'none'
            }} 
            onClick={handleBackdropClick}
            tabIndex={-1}
        >
            <div 
                className="modal-dialog modal-lg"
                style={{ margin: 0, maxWidth: '90%', width: '800px' }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title" id="commandDetailsModalLabel">
                            Détails de la commande #{command.number}
                        </h5>
                        <button 
                            type="button" 
                            className="btn-close" 
                            onClick={() => setCommand(null)}
                            aria-label="Close"
                        ></button>
                    </div>
                    <div className="modal-body">
                        <div className="row mb-3">
                            <div className="col-md-6">
                                <strong>ID Commande:</strong> <span className="text-muted">{command.id}</span>
                            </div>
                            <div className="col-md-6">
                                <strong>Numéro:</strong> <span className="text-muted">#{command.number}</span>
                            </div>
                        </div>
                        <div className="row mb-3">
                            <div className="col-md-6">
                                <strong>Statut:</strong> 
                                <span className={`badge ms-2 ${command.status === "delivered" ? "bg-success" : command.status === "paid" ? "bg-info" : "bg-warning"}`}>
                                    {convertStatusToFrench(command.status)}
                                </span>
                            </div>
                            <div className="col-md-6">
                                <strong>Date de création:</strong> {new Date(command.created_at).toLocaleDateString('fr-FR', { 
                                    day: '2-digit', 
                                    month: 'long', 
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                })}
                            </div>
                        </div>
                        {command.client && (
                            <div className="row mb-3">
                                <div className="col-12">
                                    <strong>Client:</strong> {command.client.email || command.client_id}
                                </div>
                            </div>
                        )}
                        {command.client_address && (
                            <div className="row mb-3">
                                <div className="col-12">
                                    <strong>Adresse:</strong> {command.client_address.street} {command.client_address.number} {command.client_address.complement}, {command.client_address.zip_code} {command.client_address.city} {command.client_address.country}
                                </div>
                            </div>
                        )}
                        {command.products && command.products.length > 0 && (
                            <div className="mt-4">
                                <h6>Produits ({command.products.length})</h6>
                                <div className="table-responsive">
                                    <table className="table table-sm">
                                        <thead>
                                            <tr>
                                                <th>Image</th>
                                                <th>Nom</th>
                                                <th>Quantité</th>
                                                <th>Color</th>
                                                <th>Size</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {command.products.map((product, index) => (
                                                <tr key={product.id || index}>
                                                    <td>
                                                        <img 
                                                            src={product.images?.[0]?.image_url || 'https://placehold.co/200'} 
                                                            alt={product.name}
                                                            style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '5px' }}
                                                        />
                                                    </td>
                                                    <td>{product.name}</td>
                                                    <td>{product.quantity || product.variants?.reduce((sum, v) => sum + (v.quantity || 0), 0) || 0}</td>
                                                    <td>{product.color || product.variant_color || '-'}</td>
                                                    <td>{product.size || product.variant_size || '-'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                        {(!command.products || command.products.length === 0) && (
                            <div className="alert alert-info">
                                No products in this order
                            </div>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button 
                            type="button" 
                            className="btn btn-primary" 
                            onClick={() => handleMarkAsDelivered()}
                        >
                            Marquer comme livré
                        </button>
                        <button 
                            type="button" 
                            className="btn btn-secondary" 
                            onClick={() => setCommand(null)}
                        >
                            Fermer
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}